import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import PlacePicker from '../components/wizard/PlacePicker'
import Button from '../components/common/Button'
import { estimateDelivery } from '../services/orderService'
import { readPlaces, rememberPlace } from '../utils/places'
import { makeEmptyLocation } from '../utils/payload'
import { toPrefillParams } from '../utils/prefill'
import { peso } from '../utils/copy'

/**
 * *"Magkano po padala?"*, answered in the seconds a chat conversation allows.
 *
 * Not a calculator — the front door of an order. The shop that sells over Messenger gets
 * asked mid-conversation and had two options before this: guess, or place a whole order to
 * find out. See docs/intent/customer-app-friction.md.
 *
 * ## One question per screen, because the first version was not
 *
 * This began as both pickers stacked on one page with the answer below them. That is a
 * screen the reader has to **survey** before they can answer anything — the exact shape
 * `order-form-redesign.md` tore out of the order form, reintroduced here by somebody who
 * had just read why it was removed.
 *
 * So it takes the form's own shape instead: a trail, one question, one control, one big
 * button at the bottom, identical on every screen. The person using it learns the shape
 * once. `WizardShell` cannot be reused directly — it reads `useWizard`, and this page has
 * no order and deliberately no account — so the shape is mirrored rather than imported.
 *
 * ## One size up, on purpose
 *
 * The body text here is `text-body` where the rest of the app uses `text-body-s`, and the
 * price is the largest type in the product. This screen is read by somebody holding a
 * cheap phone at arm's length while a customer waits, and it is read out loud. The house
 * scale is right for a form being filled in; this is a number being *quoted*.
 *
 * ## No account, and no rider
 *
 * Mounted outside `AuthProvider` beside `/pin`, calling a bare fetch. And it never calls
 * `POST /orders/quote` — that holds a real rider for thirty seconds, which is right on a
 * summary and ruinous on a question that may never become an order.
 */

const STEPS = ['pickup', 'dropoff', 'presyo']

const COPY = {
  pickup: {
    short: 'Saan kukuha',
    title: 'Saan po kukunin?',
    hint: 'Ang tindahan o lugar na pupuntahan ng rider.',
  },
  dropoff: {
    short: 'Saan ihahatid',
    title: 'Saan po ihahatid?',
    hint: 'Ang lugar ng customer. Hanapin po, o ituro sa mapa.',
  },
  presyo: {
    short: 'Presyo',
    title: 'Ito po ang bayad',
  },
}

/** The form's own trail, in three. Same reasoning as `StepTrail`: the count is the reassurance. */
function Trail({ index }) {
  return (
    <div className="mt-4">
      <ol className="flex items-center gap-1" aria-label="Hakbang">
        {STEPS.map((step, position) => (
          <li key={step} aria-current={position === index ? 'step' : undefined} className="flex-1">
            <span className="sr-only">{COPY[step].short}</span>
            <span
              aria-hidden="true"
              className={`block h-1 rounded-[1px] transition-colors duration-settle ease-out ${
                position < index ? 'bg-brand/40' : position === index ? 'bg-brand' : 'bg-hairline'
              }`}
            />
          </li>
        ))}
      </ol>

      <p className="mt-2 text-label font-semibold uppercase text-ink-3">
        {index + 1} sa {STEPS.length} · {COPY[STEPS[index]].short}
      </p>
    </div>
  )
}

export default function PriceCheck() {
  const { slug } = useParams()
  const navigate = useNavigate()

  const [index, setIndex] = useState(0)
  const step = STEPS[index]

  /**
   * The shop's own address is answered once and never again.
   *
   * A price checker on a shop's phone is asked about the same pickup every time — theirs.
   * The screen still appears, pre-filled, rather than being skipped: the shape staying the
   * same every visit is worth more to this reader than one screen saved, and pressing
   * *Sunod* on a filled screen is easier to see than a small "change this" link.
   */
  const [pickup, setPickup] = useState(() => {
    const held = readPlaces('pickup')[0]

    return held
      ? { query: held.label, lat: held.lat, lng: held.lng, label: held.label }
      : makeEmptyLocation()
  })
  const [pickupSource, setPickupSource] = useState(() => readPlaces('pickup')[0]?.name ?? '')
  const [dropoff, setDropoff] = useState(makeEmptyLocation())

  const [state, setState] = useState({ loading: false, result: null, error: null })

  const heading = useRef(null)

  // Focus lands on the new question, exactly as `WizardShell` does it — the page never
  // navigates, so without this a screen reader announces nothing when the step changes.
  useEffect(() => {
    heading.current?.focus()
  }, [step])

  const answered = step === 'pickup' ? pickup.lat != null : dropoff.lat != null

  const ask = async () => {
    setState({ loading: true, result: null, error: null })
    setIndex(2)

    try {
      const result = await estimateDelivery({ partnerSlug: slug, pickup, dropoff })

      /**
       * Both ends, not just the shop's own.
       *
       * A shop is asked about the same barangays over and over — the dormitory belt, the
       * two or three subdivisions their customers live in. Remembering the drop-offs they
       * have already priced is what makes the second question about Umali one tap instead
       * of a search, and it is the same list the order form offers.
       *
       * Only after the server agreed both are real, priceable places. A pin it refused is
       * not somewhere this shop has ever sent a delivery.
       */
      rememberPlace('pickup', { ...pickup, name: pickupSource })
      rememberPlace('dropoff', dropoff)

      setState({ loading: false, result, error: null })
    } catch (error) {
      // The server's own sentence, shown as written — a drop-off outside Los Baños and Bay
      // is the commonest refusal here, and the API already names which end is at fault.
      setState({ loading: false, result: null, error })
    }
  }

  const forward = () => {
    if (!answered) return

    if (step === 'pickup') return setIndex(1)

    return ask()
  }

  const back = () => {
    setState({ loading: false, result: null, error: null })
    setIndex((current) => Math.max(0, current - 1))
  }

  /**
   * The loop this page actually lives in.
   *
   * A shop answering a chat is asked the same question about a different address five
   * minutes later. Keeping the pickup and clearing only the drop-off is that next question,
   * in one press.
   */
  const askAgain = () => {
    setDropoff(makeEmptyLocation())
    setState({ loading: false, result: null, error: null })
    setIndex(1)
  }

  const proceed = () => {
    const params = toPrefillParams({
      pickup,
      pickupSource,
      dropoff,
      // Only present when a rider was actually chosen — see `exact` on the estimate.
      riderHint: result?.riderHint,
    })

    navigate(`/order/${encodeURIComponent(slug)}?${params.toString()}`)
  }

  const { loading, result, error } = state
  const copy = COPY[step]

  return (
    <main className="min-h-dvh bg-surface px-5 pb-8">
      <div className="mx-auto flex w-full max-w-[26rem] flex-col">
        <Trail index={index} />

        <h1
          ref={heading}
          tabIndex={-1}
          className="mt-3 font-display font-bold text-display-l text-ink outline-none"
        >
          {step === 'presyo' && result ? result.partner.businessName : copy.title}
        </h1>

        {step !== 'presyo' && (
          <p className="mt-1.5 text-body text-ink-2 max-w-[40ch]">{copy.hint}</p>
        )}

        <div className="mt-5">
          {step === 'pickup' && (
            <PlacePicker
              kind="pickup"
              loc={pickup}
              setLoc={setPickup}
              onPickName={setPickupSource}
            />
          )}

          {step === 'dropoff' && <PlacePicker kind="dropoff" loc={dropoff} setLoc={setDropoff} />}

          {step === 'presyo' && (
            <div aria-live="polite">
              {loading && <p className="text-body text-ink-2">Tinitingnan po…</p>}

              {error && (
                <div role="alert">
                  <p className="text-display-m font-semibold text-alert">Hindi po pwede</p>
                  <p className="mt-2 text-body text-ink-2">{error.message}</p>
                </div>
              )}

              {result && (
                <>
                  <p className="text-body text-ink-3">Bayad sa padala</p>

                  {/* The largest type in the product.
                      With a rider online this is the exact fee — the server asked the real
                      matcher who would win and priced their trip. Without one it is the top
                      of the range, never the bottom: a shop that reads out the bottom and
                      collects the top has been made to lie to their own customer. */}
                  <p className="mt-1 font-display font-bold text-[2.75rem] leading-none tabular-nums text-brand">
                    {result.exact ? peso(result.deliveryFee) : peso(result.deliveryFeeTo)}
                  </p>

                  {result.exact ? (
                    <p className="mt-3 text-body text-ink-2">
                      Eksakto po ito sa ngayon — may rider na malapit. Kung magbago ang
                      rider bago mag-order, bahagya itong pwedeng magbago.
                    </p>
                  ) : (
                    <p className="mt-3 text-body text-ink-2">
                      {result.ridersOnline === 0
                        ? 'Walang rider na naka-online ngayon, kaya tantiya muna ito. '
                        : ''}
                      Ito po ang <strong className="text-ink">pinakamataas</strong>. Kung
                      malapit ang rider sa tindahan,{' '}
                      <strong className="text-ink">{peso(result.deliveryFeeFrom)}</strong> lang.
                    </p>
                  )}

                  <p className="mt-2 text-body text-ink-3 tabular-nums">
                    {Number(result.distanceKm).toFixed(1)} km ang layo
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        {/* One row, same place, every screen. */}
        <div className="flex gap-3 mt-6">
          {step !== 'presyo' && (
            <>
              {index > 0 && (
                <Button type="button" variant="ghost" onClick={back} className="flex-none">
                  Bumalik
                </Button>
              )}

              <Button type="button" onClick={forward} disabled={!answered} className="flex-1">
                {step === 'dropoff' ? 'Tingnan ang presyo' : 'Sunod'}
              </Button>
            </>
          )}

          {/* An error gets one button, not two. "Palitan" and "Tanong ulit" both go
              backwards and a reader has to work out the difference — on the one screen
              where they are already being told something went wrong. The refusal is
              almost always a drop-off outside the two towns, and the answer to that is to
              edit the address that is still on the previous screen, not to clear it. */}
          {step === 'presyo' && !loading && error && (
            <Button type="button" onClick={back} className="flex-1">
              Palitan ang lugar
            </Button>
          )}

          {step === 'presyo' && !loading && result && (
            <>
              <Button type="button" variant="ghost" onClick={askAgain} className="flex-1">
                Tanong ulit
              </Button>

              <Button type="button" onClick={proceed} className="flex-1">
                Iorder na
              </Button>
            </>
          )}
        </div>

        {step === 'presyo' && result && (
          <p className="mt-3 text-body-s text-ink-3">
            Hihingin po ang sign-in bago mag-order. Dala na ang dalawang lugar na ito.
          </p>
        )}
      </div>
    </main>
  )
}
